import React, { useEffect, useState } from 'react';
import { X, CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
  logoSrc?: string; // Configurable logo source
  onDismiss: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  persistent = false,
  logoSrc = '/logo-no-text.svg', // Trade Loop logo (no text version)
  onDismiss
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    // Trigger enter animation. The timer is cleared on unmount: a toast dismissed inside
    // this 10ms window would otherwise setState after unmount — harmless in the browser,
    // but it throws once the surrounding environment is gone (it surfaced as an unhandled
    // "window is not defined" in tests, firing after teardown).
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto dismiss with progress bar (skip if persistent)
    if (!persistent && duration > 0 && !isPaused) {
      let elapsed = 0;
      const interval = setInterval(() => {
        elapsed += 100;
        if (elapsed >= duration) {
          handleDismiss();
          clearInterval(interval);
          return;
        }
        setProgress(prev => {
          const next = prev - (100 / (duration / 100));
          if (next <= 0) {
            return 0;
          }
          return next;
        });
      }, 100);

      return () => {
        clearInterval(interval);
      };
    }
  }, [duration, isPaused, persistent]);

  const handleMouseEnter = () => {
    setIsPaused(true);
    setProgress(100);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onDismiss(id);
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
      case 'info':
        return <Info className="w-5 h-5 text-white/90 drop-shadow-md flex-shrink-0" />;
    }
  };

  const getStyles = () => {
    const baseStyles = 'relative flex mb-3 rounded-xl shadow-2xl overflow-hidden backdrop-blur-sm transition-all duration-300 min-w-[280px] max-w-[calc(100vw-32px)] sm:min-w-[380px] sm:max-w-[480px]';

    const typeStyles = {
      success: 'bg-gradient-to-r from-green-500/95 to-emerald-600/95 text-white border border-green-400/30',
      error: 'bg-gradient-to-r from-red-500/95 to-rose-600/95 text-white border border-red-400/30',
      warning: 'bg-gradient-to-r from-amber-500/95 to-orange-600/95 text-white border border-amber-400/30',
      info: 'bg-gradient-to-r from-blue-500/95 to-indigo-600/95 text-white border border-blue-400/30'
    };

    const animationStyles = isLeaving
      ? 'transform translate-x-full opacity-0 scale-95'
      : isVisible
        ? 'transform translate-x-0 opacity-100 scale-100'
        : 'transform translate-x-full opacity-0 scale-95';

    return `${baseStyles} ${typeStyles[type]} ${animationStyles}`;
  };

  const getLogoBackground = () => {
    const styles = {
      success: 'bg-gradient-to-br from-green-600 to-green-700',
      error: 'bg-gradient-to-br from-red-600 to-red-700',
      warning: 'bg-gradient-to-br from-amber-600 to-amber-700',
      info: 'bg-gradient-to-br from-blue-600 to-blue-700'
    };
    return styles[type];
  };

  const getProgressBarColor = () => {
    const colors = {
      success: 'bg-green-300',
      error: 'bg-red-300',
      warning: 'bg-amber-300',
      info: 'bg-blue-300'
    };
    return colors[type];
  };

  return (
    <div
      className={getStyles()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo Section */}
      <div className={`flex items-center justify-center p-4 ${getLogoBackground()} shadow-inner relative overflow-hidden`}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(255,255,255,0.1) 10px,
              rgba(255,255,255,0.1) 20px
            )`
          }} />
        </div>

        {/* Logo container - configurable logo with white filter */}
        <div className="relative flex items-center justify-center">
          <img
            src={logoSrc}
            alt="Logo"
            className="w-20 h-20 object-contain brightness-0 invert filter drop-shadow-xl"
            style={{
              filter: 'brightness(0) invert(1) drop-shadow(0 10px 20px rgba(0,0,0,0.3))'
            }}
          />
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 p-5 pr-12">
        <div className="flex-1">
          <div className="flex items-center gap-2.5 pb-2 border-b border-white/30">
            {getIcon()}
            <h4 className="font-bold text-white text-base tracking-wide drop-shadow-sm">
              {title}
            </h4>
          </div>
          {message && (
            <p className="text-sm mt-2.5 text-white/90 leading-relaxed">
              {message}
            </p>
          )}
        </div>
      </div>

      {/* Progress Bar (only show if not persistent) */}
      {!persistent && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
          <div
            className={`h-full ${isPaused ? 'transition-all duration-300 ease-out' : 'transition-all duration-100 ease-linear'} ${getProgressBarColor()}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Close Button */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors hover:rotate-90 transform duration-200"
        aria-label="Dismiss notification"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Toast;
